import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import articles from './articles';
import LayoutNoLogin from '../../components/layout/LayoutNoLogin';
import Layout from '../../components/layout/StandardLayout';
import { BookOpenIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

const Breadcrumbs = () => (
    <nav className="flex items-center space-x-2 text-sm text-base-content/60">
        <Link to="/wiki" className="flex items-center hover:text-primary">
            <BookOpenIcon className="w-4 h-4 mr-1" />
            <span>Wiki</span>
        </Link>
    </nav>
);

const ArticleCard = ({ title, description, image, slug }) => (
    <Link
        to={`/wiki/${slug}`}
        className="group block overflow-hidden rounded-xl bg-base-100 border border-base-300 shadow-sm hover:border-primary/20 hover:shadow-md transition-all"
    >
        {image && (
            <div className="aspect-[2/1] w-full overflow-hidden bg-base-200">
                <img
                    src={image}
                    alt=""
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
            </div>
        )}
        <div className="p-4">
            <h3 className="text-base font-medium text-base-content group-hover:text-primary">
                {title}
            </h3>
            {description && (
                <p className="mt-1 text-sm line-clamp-3 text-base-content/70">
                    {description}
                </p>
            )}
        </div>
    </Link>
);

const NavigationTabs = ({ onScrollToSection }) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-12">
        <button
            onClick={() => onScrollToSection('allgemein')}
            className="btn btn-outline btn-primary"
        >
            Allgemeine Informationen
        </button>
        <button
            onClick={() => onScrollToSection('grundlagen')}
            className="btn btn-outline btn-primary"
        >
            Prüfungsrelevante Grundlagen
        </button>
    </div>
);

const WikiHome = () => {
    const { isAuthenticated } = useContext(AuthContext);
    const SelectedLayout = isAuthenticated ? Layout : LayoutNoLogin;

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const allgemeineArtikel = articles.filter(article => 
        !['navigation', 'schifffahrtsrecht', 'wetterkunde', 'seemannschaft'].includes(article.slug)
    );

    const grundlagenArtikel = articles.filter(article => 
        ['navigation', 'schifffahrtsrecht', 'wetterkunde', 'seemannschaft'].includes(article.slug)
    );

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navbarHeight = 64; // Höhe der Navbar
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    return (
        <SelectedLayout>
            <div className="min-h-screen bg-base-100">
                <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
                    <Breadcrumbs />
                    
                    <header className="mt-8 mb-12">
                        <h1 className="text-4xl font-bold tracking-tight text-base-content sm:text-5xl">
                            Wiki
                        </h1>
                        <p className="mt-4 text-xl text-base-content/70">
                            Alles Wissenswerte rund um die SKS-Prüfung
                        </p>
                    </header>

                    <NavigationTabs onScrollToSection={scrollToSection} />

                    <section id="allgemein" className="mb-16">
                        <h2 className="text-3xl font-bold mb-4">Allgemeine Informationen</h2>
                        <div className="bg-base-200 rounded-xl p-6 mb-8">
                            <p className="text-base-content/70">
                                Hier findest du wichtige organisatorische Informationen und Hilfestellungen rund um die SKS-Prüfung. 
                                Diese Artikel geben dir einen Überblick über den Weg zum SKS-Schein und helfen dir bei der Prüfungsvorbereitung.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {allgemeineArtikel.map((article) => (
                                <ArticleCard
                                    key={article.slug}
                                    title={article.title}
                                    description={article.description}
                                    image={article.image}
                                    slug={article.slug}
                                />
                            ))}
                        </div>
                    </section>

                    <section id="grundlagen" className="pt-8">
                        <h2 className="text-3xl font-bold mb-4">Prüfungsrelevante Grundlagen</h2>
                        <div className="bg-base-200 rounded-xl p-6 mb-8">
                            <div className="flex items-start space-x-4">
                                <div className="bg-primary/10 rounded-lg p-3">
                                    <AcademicCapIcon className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <p className="text-base-content/70">
                                        Diese Grundlagenartikel decken die wichtigsten Prüfungsthemen ab. Wir empfehlen, 
                                        diese Artikel durchzuarbeiten, bevor du mit den Prüfungsfragen beginnst, um ein 
                                        solides Fundament zu schaffen.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {grundlagenArtikel.map((article) => (
                                <ArticleCard
                                    key={article.slug}
                                    title={article.title}
                                    description={article.description}
                                    image={article.image}
                                    slug={article.slug}
                                />
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </SelectedLayout>
    );
};

export default WikiHome; 